// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState, useRef } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import useCart from 'Hooks/useCart';
import ProductQuickViewModalComponent from 'Components/Products/ProductQuickViewModalComponent';
import ModalManager from 'Containers/ModalManager/ModalManager';
import { API_PRODUCTS } from 'Constants';
import { defaultModalsState, ModalsContext } from 'Contexts/ModalsContext';
import { ToastNotificationsContext } from 'Contexts/ToastNotificationsContext';
import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';

const ProductQuickViewModal = memo(() => {
  const { modalData, setModalState, resetModal } = useContextSelector(ModalsContext, data => ({
    modalData: data.modalState[defaultModalsState.QUICK_VIEW.key],
    setModalState: data.setState,
    resetModal: () => {
      setProduct({});
      data.resetModal(defaultModalsState.QUICK_VIEW.key);
    }
  }));
  const { pathname } = useRouter();
  const { data: user } = useUser();
  const fetch = useFetch();
  const [product, setProduct] = useState({});
  const addNotification = useContextSelector(ToastNotificationsContext, data => data.addNotification);
  const cart = useCart();
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    resetModal();
  }, [pathname]);

  useEffect(() => {
    setProduct(prevProduct => ({
      ...prevProduct,
      ...(modalData.state?.product || {}),
    }));
  }, [modalData.state?.product]);

  const handleOnAddToCart = async () => {
    if (!product.hasPrice) {
      setModalState(defaultModalsState.ADD_EXTERNAL_SUPPLIER_PRODUCT_TO_CART.key, {
        open: true,
        product,
        onConfirm: () => executeAddToCart()
      });
      return null;
    }

    return executeAddToCart();
  };

  const executeAddToCart = async () => {
    try {
      const existingProd = cart.data.items?.find(item => (
        item.productId === product.id
        && !item.promotion
      ));
      const qty = (existingProd?.quantity || 0) + (product.quantity || product.minQuantity || 1);
      if (qty > ('stockValue' in product ? product.stockValue : 100)) {
        addNotification({
          type: 'warning',
          title: t('products.insufficientStock')
        });
        return null;
      }
      setAddToCartLoading(true);
      const [updated, crossProducts] = await Promise.allSettled([
        cart.updateProduct(omit({ ...product, quantity: qty }, ['promotion', 'promotionId'])),
        fetch(`${ API_PRODUCTS }/${ product.id }/cross-sells?currency=${ user?.defaultCurrency }&vat=${ user?.withVat }`)
      ]);
      if (updated.status === 'fulfilled') {
        if (updated.value === null) {
          addNotification({
            type: 'warning',
            title: t('products.dropShipmentWarning')
          });
        } else {
          addNotification({
            title: t('cart.successfullyAddedToCart')
          });
          setProduct(prod => ({
            ...prod,
            quantity: prod.minQuantity || 1
          }));
        }
      } else {
        addNotification({
          type: 'error',
          title: t('cart.failedAddToCart'),
          message: updated.value.errors?.at(0)?.message
        });
        return null;
      }
      if (
        Array.isArray(crossProducts.value)
        && crossProducts.value.length
      ) {
        setModalState(defaultModalsState.CROSS_SALES.key, {
          open: true,
          products: crossProducts.value,
          product
        });
      }
      return updated.value;
    } catch (err) {
      addNotification({
        type: 'error',
        title: t('cart.failedAddToCart'),
        message: err?.info?.errors?.at(0)?.message
      });
    } finally {
      setAddToCartLoading(false);
    }
    return null;
  };

  const handleStockAlertModalOpen = () => {
    setModalState(defaultModalsState.STOCK_ALERT.key, {
      open: true,
      productId: product.id || product.productId,
      productCode: product.code,
      productQuantity: Math.max(product.quantity, product.minQuantity)
    });
  };

  const handleOnChangeQuantity = qty => {
    const newQty = Math.min(
      product.stockValue,
      Math.max(qty, product.minQuantity || 1)
    );
    setProduct(prod => {
      if (newQty !== prod.quantity) {
        return {
          ...prod,
          quantity: newQty
        };
      }
      return prod;
    });
  };

  useEffect(() => () => {
    setProduct({});
    resetModal();
  }, []);

  if (!modalData.state.open) {
    return null;
  }

  return (
    <ModalManager open>
      <ProductQuickViewModalComponent
        product={ product }
        error={ modalData.state.error }
        onClose={ resetModal }
        addToCartLoading={ addToCartLoading }
        onStockAlertModalOpen={ handleStockAlertModalOpen }
        onChangeQuantity={ handleOnChangeQuantity }
        onAddToCart={ handleOnAddToCart }
        selectedSlide={ selectedSlide }
        onSelectedSlide={ setSelectedSlide }
      />
    </ModalManager>
  );
});

ProductQuickViewModal.displayName = 'ProductQuickViewModalContainer';

export default ProductQuickViewModal;
